import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { clearListCache } from '../hooks/useListCache';
import { CyclingEvent, EventCategory, EventEditResponse, CATEGORY_LABELS, CATEGORY_COLORS, LINK_TYPE_LABELS, LINK_TYPE_ORDER, haversineDistance, buildMapsUrl, Organizer, PublicOrganizer, displayBundesland, WeatherForecast, weatherLabel, isTimeVariant, formatDurationMinutes, durationChipParts, distanceShortLabel, toExternalUrl, eventCountdownTitle, eventCategories, eventCardModel, splitMonYear, accommodationCard, formatRegistrationStart, registrationDetailBadge, canRegisterNow, showsRegistrationLink, isFlyerImage, organizerAvatarColor, organizerInitials, NewsPostSummary, NEWS_CATEGORY_LABELS, OrganizerPortalSummary, outboundClickContext, linkDomain, showRoutePlaceholder, eventTypesLabel, distanceEventTypeLabel, distanceStartLabel, eventWithOpenProposal, permanentDateLabel, eventSeoTitle, eventSeoDescription, isPopularEvent, POPULAR_LABEL, formatParticipants, formatAddress, formatElevationMeters, showsRegistrationAlert, showsNextEditionAlert, formatKm } from '@radevents/shared';
import { RegistrationAlertButton } from '../components/RegistrationAlert';
import PriceTierList from '../components/PriceTierList';
import { CalendarCheck, ChevronLeft, Clock3, CloudSun, Flag, Mountain, RefreshCw, Route, Share2, Tag, Trash2, X } from 'lucide-react';
import EventDetailKopf, { EventKopfBild, EventKopfKachel, EventKopfStrecke, kurzerWochentag } from '../components/EventDetailKopf';
import EventDetailUnten, { ABSCHNITT_IDS, UntenLink } from '../components/EventDetailUnten';
import { AnmeldeStandChip, EventAnmeldeKarte, EventDesktopKopf, EventVeranstalterKarte, EventKennzahl, EventSprungLeiste, EventStreckenBlock, EventWetter, useOberhalbAusSicht } from '../components/EventDetailDesktop';
import { EventDetailActions, EventDetailPrimaryAction } from '../components/EventDetailMobile';
import eventService from '../services/eventService';
import organizerService from '../services/organizerService';
import organizerPortalService from '../services/organizerPortalService';
import newsService from '../services/newsService';
import { track, trackOnce, trackNewsClick } from '../services/trackingService';
import { umamiTrack } from '../services/analytics';
import toast from 'react-hot-toast';
import { EventForm, emptyEventForm, EventFormValues, PreparedEventData, ProposedGpxFile } from '../components/EventFormModal';
import EventStatsCard from '../components/EventStatsCard';
import AccommodationCard from '../components/AccommodationCard';
import { buildFormFromEvent } from '../utils/eventForm';
import { useNotifyFavoritesConfirm } from '../components/NotifyFavoritesDialog';
import GpxMap from '../components/GpxMap';
import RouteMapPlaceholder from '../components/RouteMapPlaceholder';
import PublicHeader from '../components/Layout/PublicHeader';
import { heroFocus, heroPhotoUrl } from '../utils/eventHero';
import { analyzeHeroImage, HeroShape } from '../utils/heroImageShape';
import EventHistory from '../components/EventHistory';
import AppTopbarBrand from '../components/Layout/AppTopbarBrand';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { rememberPendingFavorite } from '../services/pendingFavorite';
import { claimPath } from '../components/organizer/claimIntent';
import { formatDateTime, formatDayLong } from '../utils/format';

const formatDate = (dateStr: string) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString('de-DE', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });


const categoryLabel = (c: EventCategory) => CATEGORY_LABELS[c] || c;

/**
 * Wie viele Artikel unter dem Event stehen. Mehr als drei machen aus dem Hinweis eine zweite
 * Liste — wer alles lesen will, findet den Rest ueber die News-Seite.
 */
const MAX_RELATED_NEWS = 3;

type RouteEmbed = { platform: 'komoot' | 'alltrails'; embedUrl?: string } | null;

function buildRouteEmbed(url: string): RouteEmbed {
  try {
    const parsed = new URL(url);

    if (parsed.hostname.includes('komoot.com')) {
      const match = parsed.pathname.match(/\/tour\/(\d+)/);
      if (!match) return null;
      const tourId = match[1];
      const shareToken = parsed.searchParams.get('share_token');
      let embedUrl = `https://www.komoot.com/tour/${tourId}/embed`;
      if (shareToken) embedUrl += `?share_token=${shareToken}`;
      return { platform: 'komoot', embedUrl };
    }

    if (parsed.hostname.includes('alltrails.com')) {
      // AllTrails blockiert iframes (X-Frame-Options) — nur als Link-Karte anzeigen
      const path = parsed.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/, '');
      if (!path || path === '/') return null;
      return { platform: 'alltrails' };
    }

    return null;
  } catch {
    return null;
  }}

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  /**
   * Zurueck zur Liste: echtes History-Back, damit die gesetzten Filter (in der URL), der
   * Listen-Cache und die Scrollposition erhalten bleiben.
   *
   * Aus dem Pin-Popup der Umkreis-Karte kommt das Event in einem **neuen Tab** — der Picker
   * arbeitet auf einem Draft, den ein Wegnavigieren zerstoeren wuerde. Dort gibt es keinen
   * Verlauf, und "Zurueck" landete deshalb auf der Startseite statt auf der Karte. In diesem
   * Fall ist Zurueck das Schliessen des Tabs: darunter liegt die Karte samt offener Auswahl —
   * genau das, was der Zurueck-Knopf des Browsers hier ohnehin tut.
   *
   * Der Marker `?von=karte` muss sein: ein Deeplink aus Suche oder Messenger hat ebenfalls
   * keinen Verlauf, sein Tab gehoert aber dem Nutzer und darf nicht zugehen.
   */
  const goBackToList = () => {
    if ((window.history.state?.idx ?? 0) > 0) { navigate(-1); return; }
    if (new URLSearchParams(window.location.search).get('von') === 'karte') {
      window.close();
      // Browser duerfen das Schliessen verweigern (etwa wenn der Tab doch schon Verlauf hat).
      // Dann bleibt die Startseite das Ziel, statt dass der Knopf tot wirkt.
      window.setTimeout(() => { if (!window.closed) navigate('/'); }, 150);
      return;
    }
    navigate('/');
  };
  const { isAuthenticated, isLoading: authLoading, user, isAdmin, isAdminOrModerator } = useAuth();
  const [event, setEvent] = useState<CyclingEvent | null>(null);
  // Handy-Titel in einer Zeile: Schrift zwischen 32 und 20px, so groß wie es passt.
  // Handy und Rechner bekommen je einen eigenen Aufbau (EventDetailKopf/-Unten bzw.
  // EventDetailDesktop). Nur einer wird gezeichnet, sonst lägen zwei Karten mit derselben GPX-Datei
  // in der Seite.
  const handy = useMediaQuery('(max-width: 768px)');
  // Sprungleiste am Rechner: erscheint, sobald der Kopf oben aus dem Bild ist.
  const [kopfEl, setKopfEl] = useState<HTMLElement | null>(null);
  const kopfAusSicht = useOberhalbAusSicht(kopfEl);
  // Den Anmelde-Knopf trägt die Leiste erst, wenn auch die Anmeldekarte weggescrollt ist.
  const [karteEl, setKarteEl] = useState<HTMLElement | null>(null);
  const karteAusSicht = useOberhalbAusSicht(karteEl);
  const [selectedDistanceIdx, setSelectedDistanceIdx] = useState(0);
  // Externe Bild-URLs koennen tot sein — dann faellt die Seite still auf "kein Foto" zurueck.
  const [photoBroken, setPhotoBroken] = useState(false);
  /**
   * Darstellung des Bilds, sobald es analysiert ist (siehe utils/heroImageShape): Foto in den
   * zugeschnittenen Hero, Schriftzug unbeschnitten ueber die volle Breite, Logo als eigene
   * Kachel. null = noch nicht bekannt. Die Analyse laeuft im Browser, weil weder Upload noch
   * externe Bild-URL die Masse mitliefern.
   */
  const [heroKind, setHeroKind] = useState<HeroShape | null>(null);
  // Veranstalter-Logo (externe URL) kann tot sein — dann auf den Initial-Avatar zurueckfallen.
  const [logoBroken, setLogoBroken] = useState(false);
  const [loading, setLoading] = useState(true);

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /**
   * Bearbeiten passiert **in der Seite**, nicht in einem Fenster darueber (07.09.2026): das
   * Formular ersetzt den Inhalt, „Abbrechen" bringt ihn zurueck. Ein Fenster ueber einer Seite,
   * die ohnehin nur dieses eine Event zeigt, war eine zweite Huelle um dieselbe Sache.
   */
  const [editing, setEditing] = useState(false);
  const [proposeInitialValues, setProposeInitialValues] = useState<EventFormValues>(emptyEventForm);
  const [proposeSaving, setProposeSaving] = useState(false);
  /** Der eigene, noch offene Vorschlag zu diesem Event: das Formular setzt dort an. */
  const [openProposal, setOpenProposal] = useState<EventEditResponse | null>(null);
  const { confirmNotifyUpdate, notifyDialog } = useNotifyFavoritesConfirm();
  const [anonName, setAnonName] = useState('');
  const [anonEmail, setAnonEmail] = useState('');

  // Organizer list (needed by modal)
  const [organizers, setOrganizers] = useState<Organizer[]>([]);

  // Public profile of this event's organizer (for the social links in the sidebar card)
  const [eventOrganizer, setEventOrganizer] = useState<PublicOrganizer | null>(null);
  /**
   * Verwaltet das angemeldete Konto den Veranstalter dieses Events? Dann stehen die Zahlen auf
   * der Seite (mit Premium) bzw. der Hinweis, dass sie zu Premium gehoeren. Nur fuer Konten ohne
   * Redaktionsrolle geholt — die Redaktion sieht die Zahlen ohnehin.
   */
  const [managedOrganizer, setManagedOrganizer] = useState<OrganizerPortalSummary | null>(null);

  // Wettervorhersage (nur ab 7 Tage vor dem Event verfügbar)
  const [weather, setWeather] = useState<WeatherForecast | null>(null);

  // Artikel, die auf dieses Event zeigen — die Gegenrichtung der Verknüpfung im News-Editor.
  const [relatedNews, setRelatedNews] = useState<NewsPostSummary[]>([]);

  // Re-Enrich queue state
  const [showReEnrichModal, setShowReEnrichModal] = useState(false);
  const [reEnrichSource, setReEnrichSource] = useState('openclaw-event-enricher');
  const [reEnrichReason, setReEnrichReason] = useState('');
  const [reEnrichSubmitting, setReEnrichSubmitting] = useState(false);

  usePageTitle(event?.title ?? 'Event');

  // Bild vorab analysieren (Format + Hintergrund). Es liegt danach im Browser-Cache, das
  // eigentliche <img> zeigt es also ohne zweiten Abruf.
  const heroUrlToMeasure = event ? heroPhotoUrl(event) : null;
  useEffect(() => {
    setHeroKind(null);
    if (!heroUrlToMeasure) return;
    let cancelled = false;
    analyzeHeroImage(heroUrlToMeasure)
      .then(shape => { if (!cancelled) setHeroKind(shape); })
      .catch(() => { if (!cancelled) setPhotoBroken(true); });
    return () => { cancelled = true; };
  }, [heroUrlToMeasure]);

  useEffect(() => {
    if (!id) return;
    window.scrollTo(0, 0);
    setEventOrganizer(null);
    // Bild-Fehlerzustaende beim Event-Wechsel zuruecksetzen (Komponente wird wiederverwendet).
    setPhotoBroken(false);
    setLogoBroken(false);
    eventService.getById(Number(id))
      .then(e => {
        setEvent(e);
        trackOnce('EVENT', String(e.id), 'VIEW');
        if (e.organizerId) {
          organizerService.getPublicById(e.organizerId).then(setEventOrganizer).catch(() => {});
        }
      })
      .catch(() => {
        toast.error('Event nicht gefunden');
        navigate('/');
      })
      .finally(() => setLoading(false));
    organizerService.getAll().then(setOrganizers).catch(() => {});
    eventService.getWeather(Number(id)).then(setWeather).catch(() => setWeather(null));
    // Fehlt der Abruf oder ist er leer, entfällt der Block einfach — er ist nie Pflichtinhalt.
    setRelatedNews([]);
    newsService.getForEvent(Number(id), MAX_RELATED_NEWS)
      .then(page => setRelatedNews(page.content))
      .catch(() => setRelatedNews([]));
  }, [id, navigate]);

  const redaktion = isAdminOrModerator();
  const eventOrganizerId = event?.organizerId ?? null;
  useEffect(() => {
    if (!isAuthenticated || redaktion || eventOrganizerId == null) { setManagedOrganizer(null); return; }
    let cancelled = false;
    organizerPortalService.me()
      .then(list => { if (!cancelled) setManagedOrganizer(list.find(o => o.id === eventOrganizerId) ?? null); })
      .catch(() => { if (!cancelled) setManagedOrganizer(null); });
    return () => { cancelled = true; };
  }, [isAuthenticated, redaktion, eventOrganizerId]);

  const handleDelete = async () => {
    if (!event) return;
    setDeleting(true);
    try {
      await eventService.delete(event.id);
      toast.success('Event gelöscht');
      navigate('/events/manage');
    } catch {
      toast.error('Löschen fehlgeschlagen');
      setDeleting(false);
    }
  };

  const handleFavorite = async () => {
    if (!event) return;
    if (!isAuthenticated) {
      // Direkt zum Login statt Fehler-Toast; nach der Anmeldung zurück zu diesem Event.
      // Der Klick wird geparkt und nach dem Login/der Registrierung automatisch eingelöst.
      rememberPendingFavorite(event.id);
      navigate('/login', { state: { from: { pathname: `/events/${event.id}` } } });
      return;
    }
    try {
      if (event.favorited) {
        await eventService.removeFavorite(event.id);
      } else {
        await eventService.addFavorite(event.id);
      }
      umamiTrack('event-favorit', { id: event.id, titel: event.title, aktion: event.favorited ? 'entfernt' : 'hinzugefuegt' });
      setEvent(e => e ? { ...e, favorited: !e.favorited } : e);
      // Drop cached list snapshots so the overview and Merkliste refetch the new favorited state.
      clearListCache('landing');
      clearListCache('favorites');
    } catch {
      toast.error('Aktion fehlgeschlagen');
    }
  };

  const startEditing = async () => {
    if (!event) return;
    // Liegt schon ein eigener Vorschlag in der Prüfung, geht es dort weiter. Der neue ersetzt ihn
    // (Backend), muss also alles tragen, was der offene schon geändert hat.
    const open = isAuthenticated && !isAdminOrModerator()
      ? await eventService.getMyOpenEdit(event.id).catch(() => null)
      : null;
    setOpenProposal(open);
    setProposeInitialValues(buildFormFromEvent(open ? eventWithOpenProposal(event, open) : event));
    setEditing(true);
    // Das Formular steht an der Stelle des Inhalts — wer unten auf „Bearbeiten" tippt, soll
    // oben bei „Titel" anfangen, nicht mitten im Formular.
    window.scrollTo(0, 0);
  };

  const cancelEditing = () => {
    setEditing(false);
    setAnonName('');
    setAnonEmail('');
  };

  /**
   * Lädt die vorgeschlagenen Strecken an die passenden Distanzen des frisch angelegten Vorschlags.
   *
   * Zugeordnet wird über den Wert (km + Zeitlimit), weil die Vorschlags-Distanzen erst beim
   * Einreichen entstehen und in anderer Reihenfolge zurückkommen als sie eingegeben wurden.
   * Jede Distanz wird nur einmal vergeben, sonst bekämen zwei gleich lange Varianten dieselbe.
   *
   * @returns true, wenn mindestens ein Upload gescheitert ist (dann steht die Meldung schon).
   */
  const uploadProposedGpx = async (created: EventEditResponse, files: ProposedGpxFile[]): Promise<boolean> => {
    if (files.length === 0) return false;
    const open = [...(created.distances ?? [])];
    let failed = false;
    for (const entry of files) {
      const idx = open.findIndex(d =>
        Number(d.distanceKm) === Number(entry.distanceKm)
        && (d.durationMinutes ?? null) === (entry.durationMinutes ?? null));
      if (idx < 0) { failed = true; continue; }
      const [target] = open.splice(idx, 1);
      try {
        await eventService.uploadProposalGpx(created.id, target.id, entry.file);
      } catch {
        failed = true;
      }
    }
    if (failed) {
      toast.error(files.length === 1
        ? 'Vorschlag eingereicht, aber die GPX-Datei konnte nicht übertragen werden'
        : 'Vorschlag eingereicht, aber nicht alle GPX-Dateien konnten übertragen werden');
    }
    return failed;
  };

  const handleProposeSave = async (data: PreparedEventData) => {
    if (!event) return;
    // `saving` schon vor der Rückfrage setzen, damit der Speichern-Button nicht klickbar bleibt,
    // solange der Bestätigungsdialog offen ist.
    setProposeSaving(true);
    try {
      // Nur der direkte Admin-/Mod-Speicherweg ändert das Event sofort und kann damit
      // benachrichtigen. Ein Community-Vorschlag geht erst durch die Review.
      const update = { ...data, active: data.active ?? event.active };
      const notify = isAdminOrModerator() ? await confirmNotifyUpdate(event.id, update) : false;
      if (notify === null) return;
      if (isAdminOrModerator()) {
        const updated = await eventService.update(event.id, update, notify);
        setEvent(updated);
        toast.success(notify ? 'Event gespeichert, Nutzer werden benachrichtigt' : 'Event gespeichert');
      } else {
        const gpxFiles = data.gpxFiles ?? [];
        const created = await eventService.proposeEdit(event.id, {
          ...data,
          imagePending: !!data.imageFile,
          flyerPending: !!data.flyerFile,
          gpxPending: gpxFiles.length > 0,
          submitterName: anonName.trim() || undefined,
          submitterEmail: anonEmail.trim() || undefined,
        });
        // Foto, Flyer und Strecken können erst jetzt hochgeladen werden — vorher gibt es weder die
        // Vorschlags-ID noch die Ids der vorgeschlagenen Distanzen. Scheitert ein Upload, ist der
        // Vorschlag selbst trotzdem eingereicht; das sagen wir auch so.
        let fileFailed = await uploadProposedGpx(created, gpxFiles);
        if (data.flyerFile) {
          try {
            await eventService.uploadProposalFlyer(created.id, data.flyerFile);
          } catch (flyerErr: any) {
            fileFailed = true;
            toast.error(flyerErr.response?.data?.error
              || 'Vorschlag eingereicht, aber der Flyer konnte nicht übertragen werden');
          }
        }
        if (data.imageFile) {
          try {
            await eventService.uploadProposalImage(created.id, data.imageFile);
          } catch (imgErr: any) {
            fileFailed = true;
            toast.error(imgErr.response?.data?.error
              || 'Vorschlag eingereicht, aber das Foto konnte nicht übertragen werden');
          }
        }
        // Mit Anhängen gibt der Server erst frei, wenn wir melden, dass alle Dateien oben sind —
        // bei einem Veranstalter mit automatischer Freigabe ist die Änderung danach online.
        const hadAttachments = !!data.imageFile || !!data.flyerFile || gpxFiles.length > 0;
        const final = hadAttachments && isAuthenticated
          ? await eventService.finishProposalAttachments(created.id).catch(() => created)
          : created;
        if (!fileFailed) {
          toast.success(final.status === 'APPROVED'
            ? 'Gespeichert. Die Änderung ist online.'
            : data.imageFile
              ? 'Vorschlag samt Foto eingereicht und wird geprüft'
              : openProposal
                ? 'Dein Vorschlag ist aktualisiert und wird geprüft'
                : 'Dein Vorschlag wurde eingereicht und wird geprüft');
        }
      }
      cancelEditing();
      window.scrollTo(0, 0);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setProposeSaving(false);
    }
  };

  const handleShare = async () => {
    if (!event) return;
    const url = window.location.href;
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    if (isMobile && navigator.share) {
      try { await navigator.share({ title: event.title, text: `Schau dir dieses Event an: ${event.title} in ${displayBundesland(event.bundesland)}`, url }); } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link kopiert!');
      } catch { toast.error('Fehler beim Kopieren'); }
    }
  };

  if (loading || authLoading) {
    return (
      <div className="event-detail-page" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
        <PublicHeader />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#922C57' }} />
        </div>
      </div>
    );
  }

  if (!event) return null;

  const catColor = CATEGORY_COLORS[event.category] || '#922C57';
  // Farbe folgt der Leitkategorie, genannt werden alle: ein Volksradfahren kann gleichzeitig
  // fuer Rennrad, Gravel und MTB offen sein.
  const categoriesLabel = eventCategories(event).map(categoryLabel).join(' · ');
  // Abgeleitet aus Vormerkungen und Teilnehmerzahl (shared/src/popular.ts), kein Schalter.
  const popular = isPopularEvent(event);
  const participantsLabel = event.participants ? formatParticipants(event.participants) : null;

  // User location comes from the PLZ set on the landing page (localStorage) or the profile.
  const storedLat = (() => { const v = localStorage.getItem('radevents.userLat'); return v ? parseFloat(v) : null; })();
  const storedLng = (() => { const v = localStorage.getItem('radevents.userLng'); return v ? parseFloat(v) : null; })();
  const effectiveLat = storedLat ?? user?.latitude ?? null;
  const effectiveLng = storedLng ?? user?.longitude ?? null;
  const distanceKmFromUser = effectiveLat != null && effectiveLng != null && event.latitude != null && event.longitude != null
    ? Math.round(haversineDistance(effectiveLat, effectiveLng, event.latitude, event.longitude))
    : null;

  const apiBase = process.env.REACT_APP_API_BASE_URL || '';
  const gpxUrlFor = (distanceId: number) => `${apiBase}/api/v1/events/${event.id}/distances/${distanceId}/gpx`;

  // Dieselbe Fassung, die der Bot im Server-HTML bekommt (SeoSnippet.kt) — Fakten statt Name,
  // damit das Suchergebnis die Frage schon beantwortet (Trello HGeTp8qd).
  const pageTitle = eventSeoTitle(event);
  const pageDescription = eventSeoDescription(event);
  const canonicalUrl = `${window.location.origin}/events/${event.id}`;

  // Die API liefert interne Links nur an Mods/Admins — für alle anderen ist links bereits gefiltert.
  // Sortierung nach LINK_TYPE_ORDER, damit „Anmeldung" oben steht.
  const sortLinks = (a: { type: string }, b: { type: string }) =>
    LINK_TYPE_ORDER.indexOf(a.type) - LINK_TYPE_ORDER.indexOf(b.type);
  const allPublicLinks = (event.links ?? []).filter(l => l.visibility === 'PUBLIC').sort(sortLinks);
  const internalLinks = (event.links ?? []).filter(l => l.visibility === 'INTERNAL').sort(sortLinks);
  // Eine Aussage zum Anmeldestand — Absage/ausgebucht uebersteuern sie (siehe registrationBadge).
  const registration = registrationDetailBadge(event);

  // Anmeldung und Ausschreibung bekommen weiter oben eigene Buttons — in der Links-Liste am
  // Seitenende gehen genau die beiden unter, nach denen der Besucher eigentlich sucht.
  const registrationLink = allPublicLinks.find(l => l.type === 'REGISTRATION');
  const announcementLink = allPublicLinks.find(l => l.type === 'ANNOUNCEMENT');
  // Eigene Kanäle des Events (nicht die des Veranstalters — die stehen in der Veranstalter-Karte).
  const socialLinks = allPublicLinks.filter(l => l.type === 'INSTAGRAM' || l.type === 'FACEBOOK');
  const publicLinks = allPublicLinks.filter(
    l => l !== registrationLink && l !== announcementLink && !socialLinks.includes(l)
  );

  const registrationUrl = registrationLink?.url || event.sourceUrl || event.organizerUrl;

  /**
   * Jeder Klick, der den Besucher zum Veranstalter schickt. Geht an die eigene DB (belastbare
   * Zahlen fuer ein spaeteres Veranstalter-Reporting) und an Umami.
   */
  const trackOutbound = (ziel: string, url?: string | null) => {
    track('EVENT', String(event.id), 'CLICK', outboundClickContext(ziel, url));
    umamiTrack('event-anmeldung', { id: event.id, titel: event.title, ziel, ...(linkDomain(url) ? { ziel_domain: linkDomain(url) as string } : {}) });
  };
  // Ein PDF-Flyer laesst sich weder einbetten noch als Vorschaubild teilen — beides muss den
  // Content-Type beruecksichtigen, sonst steht auf der Seite ein kaputtes Bild und in den
  // sozialen Netzen eine tote og:image-Referenz.
  const flyerIsImage = isFlyerImage(event);
  const flyerImageUrl = flyerIsImage ? event.flyerUrl : undefined;
  // Permanentes Event ohne Startort: statt der Stadt steht das Gebiet da („Erzgebirge"), und der
  // Kartenlink sucht das Gebiet statt einer leeren Adresse.
  const areaOnly = !event.city && !!event.areaName;
  const placeLabel = areaOnly ? event.areaName : event.city;
  const mapsUrl = buildMapsUrl(event);
  const placeAddress = {
    '@type': 'PostalAddress',
    streetAddress: [event.street, event.houseNumber].filter(Boolean).join(' ') || undefined,
    addressLocality: event.city || event.areaName || undefined,
    postalCode: event.postalCode,
    addressCountry: 'DE',
  };
  const placeGeo = event.latitude && event.longitude
    ? { geo: { '@type': 'GeoCoordinates', latitude: event.latitude, longitude: event.longitude } }
    : {};
  // Ein permanentes Event hat keinen Termin — und ein schema.org-Event ohne startDate verwirft
  // Google. Es ist ein Ort, an dem man jederzeit fahren kann (wie PreviewController).
  const schemaOrg = event.permanent ? {
    '@context': 'https://schema.org',
    '@type': 'SportsActivityLocation',
    name: event.title,
    description: event.description ?? pageDescription,
    url: canonicalUrl,
    address: placeAddress,
    ...placeGeo,
    ...(flyerIsImage && event.flyerUrl ? { image: event.flyerUrl } : {}),
  } : {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.title,
    startDate: event.startTime ? `${event.eventDate}T${event.startTime}` : event.eventDate,
    ...(event.endDate && event.endDate > event.eventDate ? { endDate: event.endDate } : {}),
    description: event.description ?? pageDescription,
    url: canonicalUrl,
    eventStatus: event.cancelled
      ? 'https://schema.org/EventCancelled'
      : event.fullyBooked
        ? 'https://schema.org/EventSoldOut'
        : 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: event.locationName ?? placeLabel,
      address: placeAddress,
      ...placeGeo,
    },
    ...(event.organizer ? { organizer: { '@type': 'Organization', name: event.organizer, ...(event.organizerUrl ? { url: event.organizerUrl } : {}) } } : {}),
    // Nur ein Bild taugt als schema.org-image — ein PDF-Flyer waere dort eine kaputte Referenz.
    ...(flyerIsImage && event.flyerUrl ? { image: event.flyerUrl } : {}),
    ...(registrationUrl ? { offers: { '@type': 'Offer', url: registrationUrl, availability: event.fullyBooked ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock' } } : {}),
  };

  const categoryPath = event.category === 'GRAVEL' ? 'gravel-events' : event.category === 'MTB' ? 'mtb-events' : 'rennrad-events';
  const categoryName = event.category === 'GRAVEL' ? 'Gravel Events' : event.category === 'MTB' ? 'MTB Events' : 'Rennrad Events';
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Radsport Events', item: window.location.origin },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `${window.location.origin}/${categoryPath}` },
      { '@type': 'ListItem', position: 3, name: event.title, item: canonicalUrl },
    ],
  };

  // Countdown pill
  const countdownLabel = eventCountdownTitle(event);

  // Echtes Foto (Event- oder Veranstalter-Bild) — das generierte Card-Motiv ist hier bewusst
  // nicht gewuenscht, die Detailseite traegt ihren Inhalt selbst.
  const photoUrl = heroPhotoUrl(event);
  // Der Bildnachweis gehoert zum Event-Foto; steht dort das Veranstalter-Bild, passt er nicht.
  const isEventPhoto = !!event.imageUrl && photoUrl === event.imageUrl;
  const photoCredit = isEventPhoto ? event.imageCredit : null;
  // KI-Bilder werden immer ausgewiesen, auch wenn kein Bildnachweis gepflegt wurde.
  const photoIsAi = isEventPhoto && !!event.imageAiGenerated;

  const hasPhoto = !!photoUrl && !photoBroken;
  // Kategorie und Countdown liegen nur auf einem Foto im Hero; sonst gehoeren sie in die Kopfzeile.
  // Anmeldestand und Glocke stehen im Kopf, auf dem Desktop und im eigenen Kopf der Handy-Ansicht.
  const registrationChip = registration ? <AnmeldeStandChip stand={registration} /> : null;
  const onAlertEnabled = () => {
    setEvent(e => e ? { ...e, favorited: true } : e);
    clearListCache('landing');
    clearListCache('favorites');
  };
  // Ausgebucht: statt „Zum Veranstalter“ die Benachrichtigung fürs nächste Jahr.
  const nextEditionAlert = showsNextEditionAlert(event);
  const alertButton = nextEditionAlert ? (
    <RegistrationAlertButton event={event} nextEdition onEnabled={onAlertEnabled} />
  ) : showsRegistrationAlert(event) ? (
    <RegistrationAlertButton event={event} onEnabled={onAlertEnabled} />
  ) : null;
  // Auf dem Handy trägt bei „ausgebucht“ der Hauptknopf unten die Benachrichtigung, oben nicht noch einmal.
  const mobileAlertButton = nextEditionAlert ? null : alertButton;
  // Verwalter ohne Premium sehen, was fehlt (siehe EventStatsCard).
  const statsCard = (redaktion || managedOrganizer) ? (
    <EventStatsCard
      eventId={event.id}
      audience={redaktion ? 'redaktion' : 'veranstalter'}
      locked={!redaktion && !managedOrganizer?.premium}
      organizerId={event.organizerId}
    />
  ) : null;

  // Mit den abweichenden Arten einzelner Strecken: „Gravel-Tour · Gravel-Race".
  const eventTypeLabel = eventTypesLabel(event);

  // ── Kopf der Handy-Ansicht (EventDetailKopf) ──────────────────────────────
  // Bild: GPX der gewählten Strecke, sonst Foto, sonst Karte vom Startort, sonst nichts.
  const kopfStrecke = event.distances[selectedDistanceIdx];
  const kopfGpx = kopfStrecke?.hasGpx ? kopfStrecke : null;
  // Hat eine andere Strecke eine Karte, diese aber nicht: die Fläche bleibt stehen und sagt es, statt
  // beim Umschalten auf Foto oder Startort zu springen.
  const kopfOhneKarte = !kopfGpx && event.distances.some(d => d.hasGpx);
  const kopfBild: EventKopfBild = kopfGpx
    ? { art: 'gpx', url: gpxUrlFor(kopfGpx.id), titel: `${distanceShortLabel(kopfGpx)}${formatElevationMeters(kopfGpx.elevationMeters) ? ` · ${formatElevationMeters(kopfGpx.elevationMeters)}` : ''}`, routeUrl: kopfGpx.routeUrl || undefined }
    : kopfOhneKarte
      ? { art: 'leer', text: 'Für diese Strecke gibt es noch keine Karte.' }
    : hasPhoto && heroKind === 'photo' && photoUrl
      ? { art: 'foto', url: photoUrl, fokus: heroFocus(event), nachweis: photoCredit, ki: photoIsAi, onFehler: () => setPhotoBroken(true) }
      : event.latitude != null && event.longitude != null && !areaOnly
        ? { art: 'ort', lat: event.latitude, lng: event.longitude }
        : null;
  const kopfStrecken: EventKopfStrecke[] = event.distances.map((d, i) => {
    const time = isTimeVariant(d);
    const [gross, einheit] = time ? durationChipParts(d.durationMinutes!) : [formatKm(d.distanceKm), 'km'];
    const unter = [
      formatElevationMeters(d.elevationMeters),
      distanceStartLabel(d.startTime),
      distanceEventTypeLabel(event.eventType, d.eventType),
      time && d.distanceKm > 0 ? `${formatKm(d.distanceKm)} km Runde` : d.label,
    ].filter(Boolean).join(' · ');
    return { key: d.id ?? i, gross, einheit, unter: unter || null };
  });
  const kopfModel = eventCardModel(event);
  const kopfDatum = kopfModel.date
    ? { monat: splitMonYear(kopfModel.date.mon).month, tag: kopfModel.date.day, wochentag: kurzerWochentag(kopfModel.date.weekday) }
    : null;
  const kopfKacheln: EventKopfKachel[] = [];
  // Ohne Startzeit keine Kachel: „noch offen“ stand bei den meisten Events und sagte nichts.
  if (event.startTime) kopfKacheln.push({ icon: <Clock3 size={14} aria-hidden />, label: 'Start', wert: `${event.startTime}${event.startTimeEnd ? ` bis ${event.startTimeEnd}` : ''} Uhr` });
  {
    const kms = event.distances.filter(d => !isTimeVariant(d) && d.distanceKm > 0).map(d => d.distanceKm);
    const n = event.distances.length;
    // Ab zwei Strecken zeigt die Wahl unter dem Bild jede einzeln; eine Kachel „38 bis 76 km“ wäre doppelt.
    if (n > 1) { /* stehen in der Streckenreihe des Kopfs */ } else if (kms.length > 0) {
      const lo = Math.min(...kms); const hi = Math.max(...kms);
      kopfKacheln.push({ icon: <Route size={14} aria-hidden />, label: n > 1 ? 'Strecken' : 'Strecke', wert: lo === hi ? `${lo} km` : `${lo} bis ${hi} km`, zusatz: n > 1 ? `${n} zur Wahl` : undefined });
    } else if (n > 0) {
      kopfKacheln.push({ icon: <Route size={14} aria-hidden />, label: n > 1 ? 'Varianten' : 'Dauer', wert: distanceShortLabel(event.distances[0]), zusatz: n > 1 ? `${n} zur Wahl` : undefined });
    }
    const hms = event.distances.map(d => d.elevationMeters || 0).filter(h => h > 0);
    if (hms.length > 0) {
      kopfKacheln.push({ icon: <Mountain size={14} aria-hidden />, label: 'Höhenmeter', wert: `${hms.length > 1 ? 'bis ' : ''}${formatElevationMeters(Math.max(...hms))}` });
    }
  }
  if (weather && !event.permanent) {
    kopfKacheln.push({ icon: <CloudSun size={14} aria-hidden />, label: 'Wetter', wert: `${Math.round(weather.tempMax)}° / ${Math.round(weather.tempMin)}°`, zusatz: weatherLabel(weather.condition) });
  } else if (kopfModel.registration && kopfKacheln.length < 4) {
    kopfKacheln.push({ icon: <CalendarCheck size={14} aria-hidden />, label: 'Anmeldung', wert: kopfModel.registration.label.replace(/^Anmeldung\s+/, ''), ton: kopfModel.registration.tone === 'positive' ? 'positiv' : undefined });
  }
  const kopfAdresse = formatAddress(undefined, event.street, event.houseNumber, event.postalCode, event.city);
  // Steht die Adresse schon im Ortsnamen (Bestand: „Am Waldspielplatz 3, 99097 Erfurt“), nicht doppelt.
  const kopfWorte = (t: string) => t.toLowerCase().split(/[^a-z0-9äöüß]+/).filter(Boolean);
  const kopfName = event.locationName && !(kopfAdresse && kopfWorte(kopfAdresse).every(w => kopfWorte(event.locationName!).includes(w)))
    ? event.locationName : null;
  const kopfOrt = areaOnly
    ? { zeile1: placeLabel || '', zeile2: 'Gebiet', mapsUrl }
    : (event.locationName || kopfAdresse)
      ? {
        zeile1: kopfName || event.locationName || kopfAdresse,
        zeile2: [kopfName ? kopfAdresse : event.bundesland ? displayBundesland(event.bundesland) : null, distanceKmFromUser != null ? `ca. ${distanceKmFromUser} km entfernt` : null].filter(Boolean).join(' · ') || undefined,
        mapsUrl,
      }
      : undefined;
  // Sprechblase auf der Startort-Karte: kurz, eine Zeile Name oder Straße, eine Zeile Ort.
  const kopfStrasse = [event.street, event.houseNumber].filter(Boolean).join(' ');
  const kopfBlase = !areaOnly && mapsUrl && (kopfName || kopfStrasse || event.city) ? {
    titel: kopfName || kopfStrasse || event.city,
    unter: (kopfName || kopfStrasse) ? [event.postalCode, event.city].filter(Boolean).join(' ') || undefined : (event.bundesland ? displayBundesland(event.bundesland) : undefined),
    mapsUrl,
  } : null;
  /**
   * Anmelde-Button: sichtbar, wenn man sich anmelden kann — und bei geschlossener Anmeldung, weil
   * dort meist steht, ob vor Ort noch etwas geht (siehe showsRegistrationLink). Dann aber leise:
   * ein Sog auf ein geschlossenes Formular wäre ein falsches Versprechen.
   */
  const showRegistrationButton = !!registrationLink && showsRegistrationLink(event);
  const registrationButtonQuiet = !canRegisterNow(event);
  const mobileChipRedundant = event.cancelled || event.fullyBooked
    || (registration?.tone === 'positive' && showRegistrationButton && !registrationButtonQuiet);
  // Handy-Leiste: steht eine Anmeldung, führt der große Knopf dorthin. Sonst zum Veranstalter,
  // bevorzugt auf seine eigene Website, ersatzweise auf sein Profil bei uns.
  const mobilePrimaryAction: EventDetailPrimaryAction | undefined = showRegistrationButton && registrationLink
    ? { label: registrationButtonQuiet ? 'Zur Anmelde-Seite' : (registrationLink.label || 'Zur Anmeldung'), href: toExternalUrl(registrationLink.url), onClick: () => trackOutbound('anmeldung', registrationLink.url) }
    : event.organizerUrl
      ? { label: 'Zum Veranstalter', href: toExternalUrl(event.organizerUrl), onClick: () => trackOutbound('veranstalter-seite', event.organizerUrl) }
      : event.organizerId ? { label: 'Zum Veranstalter', href: `/veranstalter/${event.organizerId}`, internal: true } : undefined;

  // Shared token styles
  const cardStyle: React.CSSProperties = { background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14 };
  const monoLabel: React.CSSProperties = { fontFamily: "'Space Mono',monospace", fontSize: 12, letterSpacing: '0.12em', color: '#2C2C2C', textTransform: 'uppercase' };
  const monoStat: React.CSSProperties = { fontFamily: "'Space Mono',monospace", fontSize: 10, letterSpacing: '0.1em', color: '#9A9494', textTransform: 'uppercase' };

  // Veranstalter-Avatar: Logo, wenn vorhanden und ladbar — sonst Initial-Buchstabe im Markenkreis.
  /**
   * Dieselbe Kachel wie auf der Veranstalterseite und in der Liste.
   *
   * Vorher stand hier die **Kategoriefarbe** des Events und nur der erste Buchstabe — derselbe
   * Verein sah damit auf der Event-Seite rot mit „R" aus und auf seinem Profil gold mit „RK".
   * Die Kachel soll den Veranstalter wiedererkennbar machen, nicht die Disziplin des Termins;
   * dafür gibt es die Farbmarke oben an der Seite.
   */
  const organizerAvatar = event.organizerLogoUrl && !logoBroken ? (
    <img src={event.organizerLogoUrl} alt={event.organizer || 'Logo'} onError={() => setLogoBroken(true)}
      style={{
        width: 46, height: 46, flexShrink: 0, borderRadius: '50%', objectFit: 'contain',
        background: '#fff', border: '1px solid #E5E1E1', padding: 5, boxSizing: 'border-box',
      }} />
  ) : (
    <div style={{ width: 46, height: 46, flexShrink: 0, borderRadius: '50%', background: organizerAvatarColor(event.organizer || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 17, color: '#fff' }}>
      {organizerInitials(event.organizer || '')}
    </div>
  );


  // ── Gemeinsam für Handy und Rechner ───────────────────────────────────────────────────────
  const kalenderLink = event.permanent ? undefined : {
    url: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/api/v1/events/${event.id}/ical`,
    filename: `${event.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.ics`,
    onClick: () => umamiTrack('event-ical', { id: event.id, titel: event.title }),
  };
  // Das Datum steht in der Kachel; die Zeile nur, wo die Kachel nicht alles sagt.
  const kopfWann = event.permanent ? permanentDateLabel(event) || 'Permanent' : event.endDate && event.endDate > event.eventDate ? `bis ${formatDate(event.endDate)}` : undefined;
  const kopfStatus: { text: string; ton: 'rot' | 'gelb' } | undefined = event.cancelled ? { text: 'Abgesagt. Findet nicht statt.', ton: 'rot' } : event.fullyBooked ? { text: 'Ausgebucht. Keine Anmeldung mehr möglich.', ton: 'rot' } : event.tentative ? { text: 'Vorläufiger Termin, noch nicht bestätigt.', ton: 'gelb' } : undefined;
  /** Links unter der Beschreibung. Am Rechner steht ein Flyer-Bild als eigener Abschnitt, nicht als Link. */
  const untenLinks = (rechner: boolean): UntenLink[] => ([
    announcementLink && { key: 'ausschreibung', label: announcementLink.label || 'Ausschreibung', href: toExternalUrl(announcementLink.url), art: 'dokument', onClick: () => trackOutbound('ausschreibung', announcementLink.url) },
    event.organizerUrl && mobilePrimaryAction?.href !== toExternalUrl(event.organizerUrl) && { key: 'website', label: 'Website des Veranstalters', href: toExternalUrl(event.organizerUrl), art: 'web', onClick: () => trackOutbound('veranstalter-seite', event.organizerUrl) },
    event.flyerUrl && !(rechner && flyerIsImage) && { key: 'flyer', label: flyerIsImage ? 'Flyer' : 'Flyer (PDF)', href: event.flyerUrl, art: 'dokument', onClick: () => trackOutbound('flyer', event.flyerUrl) },
    ...socialLinks.map((l, i) => ({ key: `kanal-${l.id ?? i}`, label: l.label || LINK_TYPE_LABELS[l.type], href: toExternalUrl(l.url), art: 'kanal' as const, onClick: () => trackOutbound(`link:${l.type}`, l.url) })),
    ...publicLinks.map((l, i) => ({ key: `link-${l.id ?? i}`, label: l.label || LINK_TYPE_LABELS[l.type], href: toExternalUrl(l.url), art: 'link' as const, onClick: () => trackOutbound(`link:${l.type}`, l.url) })),
  ].filter(Boolean) as UntenLink[]).filter((l, i, all) => all.findIndex(x => x.href === l.href) === i);
  const untenUnterkunft = accommodationCard(event) ? <AccommodationCard event={event} variant="zeile" /> : null;
  const untenArtikel = relatedNews.map(post => ({
    key: post.id, href: `/news/${post.slug}`, titel: post.title, bild: post.coverImageUrl,
    meta: `${NEWS_CATEGORY_LABELS[post.category] ?? post.category}${post.publishedAt ? ` · ${formatDayLong(post.publishedAt)}` : ''}`,
    onClick: () => trackNewsClick(post.slug, 'event-detail'),
  }));
  const untenVeranstalter = (event.organizer || event.organizerId) ? {
    name: event.organizer || 'Veranstalter',
    avatar: organizerAvatar,
    href: event.organizerId ? `/veranstalter/${event.organizerId}` : undefined,
    verifiziert: eventOrganizer?.verified,
    claim: eventOrganizer && !eventOrganizer.verified && event.organizerId ? () => navigate(claimPath(event.organizerId!)) : undefined,
  } : null;

  // ── Rechner (EventDetailDesktop) ──────────────────────────────────────────────────────────
  // Ausgebucht: „Fürs nächste Jahr benachrichtigen“ steht am Rechner im Hinweis, weil er die
  // Antwort darauf ist. Die Anmeldekarte behält ihren Knopf zum Veranstalter; als breiter
  // Hauptknopf brach der lange Text dort auf zwei Zeilen um.
  const desktopStatus: { text: string; ton: 'rot' | 'gelb'; aktion?: React.ReactNode } | undefined = kopfStatus && nextEditionAlert && event.fullyBooked && !event.cancelled
    ? { ...kopfStatus, aktion: <RegistrationAlertButton event={event} nextEdition onEnabled={onAlertEnabled} /> }
    : kopfStatus;
  const desktopTermin = event.permanent ? permanentDateLabel(event) || 'Permanent' : formatDate(event.eventDate);
  const desktopOrt = placeLabel ? `${placeLabel}${!areaOnly && event.bundesland ? `, ${displayBundesland(event.bundesland)}` : ''}` : undefined;
  // Logo und Schriftzug stehen neben dem Titel; ein Foto kommt in den Strecken-Block (wie auf dem Handy).
  const desktopLogo = hasPhoto && photoUrl && (heroKind === 'logo' || heroKind === 'banner')
    ? { url: photoUrl, alt: `Logo ${event.title}`, onFehler: () => setPhotoBroken(true) } : null;
  // Eine Strecke ohne GPX, aber mit Komoot- oder AllTrails-Link: am Rechner eingebettet.
  const streckeEmbed = kopfStrecke && !kopfStrecke.hasGpx && kopfStrecke.routeUrl ? buildRouteEmbed(kopfStrecke.routeUrl) : null;
  // Links steht nur, was eine Strecke zeigt: GPX, eingebettete Strecke oder der Hinweis, dass dieser
  // Strecke die Karte fehlt. Foto und Startort haben am Rechner eigene Plätze (Kopf, Anmeldekarte),
  // damit eine GPX-Datei das Foto nicht mehr verdrängt.
  const desktopBild: EventKopfBild = kopfGpx
    ? kopfBild
    : streckeEmbed?.embedUrl
      ? { art: 'einbettung', url: streckeEmbed.embedUrl, titel: `Strecke ${distanceShortLabel(kopfStrecke)}` }
      : kopfOhneKarte ? kopfBild : null;
  const desktopFoto = hasPhoto && heroKind === 'photo' && photoUrl
    ? { url: photoUrl, fokus: heroFocus(event), nachweis: photoCredit, ki: photoIsAi, onFehler: () => setPhotoBroken(true) } : null;
  // Die Anmeldekarte zeigt den Startort immer, auch neben einer Streckenkarte: die zeigt den
  // Kurs, nicht wo man hinfährt. Auf dem Handy ersetzt die Strecke die Startort-Karte.
  const desktopOrtKarte = event.latitude != null && event.longitude != null && !areaOnly
    ? { lat: event.latitude, lng: event.longitude } : null;
  const desktopRouteLink = kopfStrecke?.routeUrl ? {
    label: streckeEmbed?.platform === 'alltrails' ? 'In AllTrails öffnen' : streckeEmbed?.platform === 'komoot' ? 'In Komoot öffnen' : 'Strecke öffnen',
    href: toExternalUrl(kopfStrecke.routeUrl),
  } : null;
  // Kennzahlen der gewählten Strecke, einmal: nicht noch einmal am Höhenprofil und nicht in den Reitern.
  const desktopKennzahlen: EventKennzahl[] = [];
  if (kopfStrecke) {
    const time = isTimeVariant(kopfStrecke);
    desktopKennzahlen.push(time
      ? { icon: <Clock3 size={14} aria-hidden />, label: 'Dauer', wert: formatDurationMinutes(kopfStrecke.durationMinutes!) }
      : { icon: <Route size={14} aria-hidden />, label: 'Distanz', wert: `${formatKm(kopfStrecke.distanceKm)} km` });
    if (time && kopfStrecke.distanceKm > 0) desktopKennzahlen.push({ icon: <Route size={14} aria-hidden />, label: 'Runde', wert: `${formatKm(kopfStrecke.distanceKm)} km` });
    const hm = formatElevationMeters(kopfStrecke.elevationMeters);
    if (hm) desktopKennzahlen.push({ icon: <Mountain size={14} aria-hidden />, label: 'Höhenmeter', wert: hm });
    if (kopfStrecke.startTime) desktopKennzahlen.push({ icon: <Clock3 size={14} aria-hidden />, label: 'Start', wert: `${kopfStrecke.startTime} Uhr` });
    const art = distanceEventTypeLabel(event.eventType, kopfStrecke.eventType);
    if (art) desktopKennzahlen.push({ icon: <Flag size={14} aria-hidden />, label: 'Eventart', wert: art });
    if (kopfStrecke.label && !time) desktopKennzahlen.push({ icon: <Tag size={14} aria-hidden />, label: 'Strecke', wert: kopfStrecke.label });
  }
  const desktopWetter = weather && !event.permanent ? <EventWetter wetter={weather} /> : null;
  const desktopAbschnitte = [
    (desktopBild || event.distances.length > 0) && { id: 'event-strecke', label: 'Strecke' },
    event.description && { id: ABSCHNITT_IDS.ueber, label: 'Über das Event' },
    untenVeranstalter && { id: ABSCHNITT_IDS.veranstalter, label: 'Veranstalter' },
    untenUnterkunft && { id: ABSCHNITT_IDS.unterkunft, label: 'Unterkunft' },
    untenArtikel.length > 0 && { id: ABSCHNITT_IDS.artikel, label: 'Artikel' },
  ].filter(Boolean) as { id: string; label: string }[];

  const desktopKopf = (
  <div ref={setKopfEl} className="edd-kopf-platz">
    <EventDesktopKopf
      datum={kopfDatum}
      farbe={catColor}
      kicker={`${categoriesLabel}${eventTypeLabel ? ` · ${eventTypeLabel}` : ''}`}
      titel={event.title}
      abgesagt={event.cancelled}
      beliebt={popular ? POPULAR_LABEL : undefined}
      ort={desktopOrt}
      countdown={countdownLabel && !event.cancelled && !event.permanent ? countdownLabel : undefined}
      status={desktopStatus}
      logo={desktopLogo}
      foto={desktopFoto}
      aktionen={{
        vorgemerkt: !!event.favorited,
        onVormerken: handleFavorite,
        onTeilen: handleShare,
        kalender: kalenderLink,
        extra: (isAdminOrModerator() || isAdmin()) ? (<>
          {isAdminOrModerator() && <button type="button" className="edd-aktion" onClick={() => setShowReEnrichModal(true)}><RefreshCw size={16} aria-hidden />Re-Enrich</button>}
          {isAdmin() && <button type="button" className="edd-aktion edd-aktion--leise" onClick={() => setShowDeleteConfirm(true)}><Trash2 size={16} aria-hidden />Löschen</button>}
        </>) : undefined,
      }}
    />
  </div>
  );

  const adminInfo = isAdmin() ? (
    <div style={{ ...cardStyle, padding: 24 }}>
      <div style={{ ...monoLabel, marginBottom: 14 }}>ADMIN-INFO</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 32, rowGap: 14, fontSize: 13, color: '#4A4A4A' }}>
        <div>
          <div style={{ ...monoStat, marginBottom: 4 }}>Status</div>
          <div>{event.status}</div>
        </div>
        <div>
          <div style={{ ...monoStat, marginBottom: 4 }}>Koordinaten</div>
          <div>{event.latitude != null && event.longitude != null ? `${event.latitude.toFixed(5)}, ${event.longitude.toFixed(5)}` : '—'}</div>
        </div>
        {event.sources && event.sources.length > 0 && (
          <div>
            <div style={{ ...monoStat, marginBottom: 4 }}>Quellen</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {event.sources.map(s => (
                <span key={s} style={{ padding: '2px 6px', borderRadius: 6, background: '#F5F3F3', color: '#6A6A6A', border: '1px solid #E5E1E1', whiteSpace: 'nowrap' }}>{s}</span>
              ))}
            </div>
          </div>
        )}
        {event.registrationStart && (
          <div>
            <div style={{ ...monoStat, marginBottom: 4 }}>Anmeldestart</div>
            <div>{formatRegistrationStart(event.registrationStart)}</div>
          </div>
        )}
        {event.externalId && (
          <div>
            <div style={{ ...monoStat, marginBottom: 4 }}>Externe ID</div>
            <div style={{ fontFamily: "'Space Mono',monospace", wordBreak: 'break-all' }}>{event.externalId}</div>
          </div>
        )}
        {event.sourceUrl && (
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ ...monoStat, marginBottom: 4 }}>Quell-URL</div>
            <a href={toExternalUrl(event.sourceUrl)} target="_blank" rel="noopener noreferrer" style={{ color: '#922C57', wordBreak: 'break-all' }}>{event.sourceUrl}</a>
          </div>
        )}
        {event.organizerUrl && (
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ ...monoStat, marginBottom: 4 }}>Veranstaltungs-URL</div>
            <a href={toExternalUrl(event.organizerUrl)} target="_blank" rel="noopener noreferrer" style={{ color: '#922C57', wordBreak: 'break-all' }}>{event.organizerUrl}</a>
          </div>
        )}
        {internalLinks.length > 0 && (
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ ...monoStat, marginBottom: 4 }}>Interne Links</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {internalLinks.map((link, i) => (
                <a key={link.id ?? i} href={toExternalUrl(link.url)} target="_blank" rel="noopener noreferrer" style={{ color: '#922C57', wordBreak: 'break-all' }}>
                  <span style={{ color: '#6A6A6A' }}>[{LINK_TYPE_LABELS[link.type]}]</span> {link.url}
                </a>
              ))}
            </div>
          </div>
        )}
        {event.komootCollectionUrl && (
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ ...monoStat, marginBottom: 4 }}>Komoot Collection</div>
            <a href={toExternalUrl(event.komootCollectionUrl)} target="_blank" rel="noopener noreferrer" style={{ color: '#922C57', wordBreak: 'break-all' }}>{event.komootCollectionUrl}</a>
          </div>
        )}
        <div>
          <div style={{ ...monoStat, marginBottom: 4 }}>Erstellt</div>
          <div>{event.createdAt ? new Date(event.createdAt).toLocaleString('de-DE') : '—'}</div>
        </div>
        <div>
          <div style={{ ...monoStat, marginBottom: 4 }}>Event-ID</div>
          <div style={{ fontFamily: "'Space Mono',monospace" }}>{event.id}</div>
        </div>
      </div>
    </div>
  
  ) : null;

  return (
    <div className="event-detail-page" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        {(photoUrl || flyerImageUrl) && <meta property="og:image" content={photoUrl ?? flyerImageUrl} />}
        <meta name="twitter:card" content={(photoUrl || flyerImageUrl) ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {(photoUrl || flyerImageUrl) && <meta name="twitter:image" content={photoUrl ?? flyerImageUrl} />}
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <style>{`
        /* Abgesagt: das Bild trägt den Status mit, ohne extra Platz zu kosten. */
        .ed-cancelled-img { filter: grayscale(0.55) brightness(0.82); }
        /* Wortmarke in der Handy-Topbar nur, wenn neben Zurueck-Pfeil und „Bearbeiten"
           noch Platz dafuer ist; darunter traegt die Bildmarke allein die Marke. */
        .ed-topbar-wordmark { display: none; }
        @media (min-width: 390px) { .ed-topbar-wordmark { display: inline; } }
      `}</style>

      <div className="hide-mobile">
        <PublicHeader />
      </div>

      {/* Phone-only app top bar */}
      {/* Handy: keine Kopfleiste über dem Bild, nur Zurück und Teilen schwebend darüber (wie Strava,
          Airbnb). Beim Bearbeiten bleibt die Leiste mit „Abbrechen“. */}
      {editing ? (
        <div className="app-topbar hide-desktop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <AppTopbarBrand onBack={goBackToList} />
          <button className="ed-edit-action" onClick={cancelEditing}><X size={16} aria-hidden />Abbrechen</button>
        </div>
      ) : (
        <div className="ed-schwebend hide-desktop">
          <button className="ed-schwebend-knopf" onClick={goBackToList} aria-label="Zurück"><ChevronLeft size={22} aria-hidden /></button>
          <button className="ed-schwebend-knopf" onClick={handleShare} aria-label="Teilen"><Share2 size={20} aria-hidden /></button>
        </div>
      )}

      {/* Phone-only status banner — der wichtigste Einzelinfo ganz oben, deutlich */}
      {(event.cancelled || event.fullyBooked || event.tentative) && (
        <div className="hide-desktop ed-band-alt" style={{
          fontFamily: "'Space Mono',monospace", fontWeight: 700, fontSize: 12, letterSpacing: '0.08em',
          padding: '11px 16px', textAlign: 'center',
          background: (event.cancelled || event.fullyBooked) ? '#C0392B' : 'rgba(217,159,26,0.16)',
          color: event.tentative && !event.cancelled && !event.fullyBooked ? '#8A6100' : '#fff',
        }}>
          {event.cancelled ? 'ABGESAGT · Findet nicht statt' : event.fullyBooked ? 'AUSGEBUCHT · Keine Anmeldung mehr möglich' : 'VORLÄUFIGER TERMIN · Noch nicht bestätigt'}
        </div>
      )}
      {/* Permanent: die Auskunft, die sonst das Datum gibt — still, kein Warnton. */}
      {event.permanent && !event.cancelled && !event.fullyBooked && (
        <div className="hide-desktop ed-band-alt" style={{
          fontFamily: "'Space Mono',monospace", fontWeight: 700, fontSize: 12, letterSpacing: '0.08em',
          padding: '11px 16px', textAlign: 'center', background: '#EFEBEB', color: '#4A4A4A',
        }}>
          {permanentDateLabel(event)?.toUpperCase()}
        </div>
      )}

      <div className="app-page" style={{ flex: 1, width: '100%', maxWidth: 1180, margin: '0 auto', padding: '22px 40px 60px', boxSizing: 'border-box' }}>

        {/* Breadcrumb, nur Desktop: mobil führt der Zurück-Knopf oben zurück. */}
        <div onClick={goBackToList} className="hide-mobile"
          style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, letterSpacing: '0.06em', color: '#6A6A6A', cursor: 'pointer', marginBottom: 18, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          ‹ EVENTS / <span style={{ color: '#2C2C2C' }}>{event.title}</span>{editing && ' / BEARBEITEN'}
        </div>

        {editing ? (
          /* Inline-Bearbeitung: dasselbe Formular wie im Fenster der Admin-Liste, nur als Karte in
             der Seite. Breite wie ein Lesetext — ueber die volle Seitenbreite gezogen laegen die
             zwei Spalten des Formulars zu weit auseinander. */
          <div style={{ ...cardStyle, padding: 24, maxWidth: 820 }}>
            {openProposal && (
              <div data-testid="open-proposal-hint" className="mb-4 p-3 rounded-lg text-sm"
                style={{ background: '#F5F3F3', border: '1px solid #E5E1E1', color: '#4A4A4A' }}>
                <strong style={{ color: '#2C2C2C' }}>Dein Vorschlag wird noch geprüft.</strong>{' '}
                Hier steht er mit allen Änderungen vom {formatDateTime(openProposal.createdAt)}.
                Was du jetzt speicherst, ersetzt ihn.
              </div>
            )}
            <EventForm
              modalTitle={isAdminOrModerator() ? 'Event bearbeiten' : 'Bearbeitung vorschlagen'}
              saveLabel={isAdminOrModerator() ? 'Speichern' : 'Vorschlag einreichen'}
              initialValues={proposeInitialValues}
              editingId={event.id}
              organizers={organizers}
              onOrganizersRefresh={() => organizerService.getAll().then(setOrganizers).catch(() => {})}
              saving={proposeSaving}
              onSave={handleProposeSave}
              onClose={cancelEditing}
              extraFooter={!isAuthenticated && !isAdminOrModerator() ? (
                <div className="border-t pt-4 mt-2" style={{ borderColor: 'var(--surface-border)' }}>
                  <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                    Optional: Deine Kontaktdaten damit wir bei Rückfragen Kontakt aufnehmen können.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      placeholder="Dein Name (optional)"
                      value={anonName}
                      onChange={e => setAnonName(e.target.value)}
                      className="w-full sm:flex-1 rounded-lg px-3 py-2 text-sm"
                      style={{ background: 'var(--input-bg, var(--surface-bg))', border: '1px solid var(--surface-border)', color: 'var(--text-primary)' }}
                    />
                    <input
                      type="email"
                      placeholder="E-Mail (optional)"
                      value={anonEmail}
                      onChange={e => setAnonEmail(e.target.value)}
                      className="w-full sm:flex-1 rounded-lg px-3 py-2 text-sm"
                      style={{ background: 'var(--input-bg, var(--surface-bg))', border: '1px solid var(--surface-border)', color: 'var(--text-primary)' }}
                    />
                  </div>
                </div>
              ) : undefined}
            />
          </div>
        ) : (<>

        {handy ? (<>
          {/* Handy: Kopf nach dem Entwurf vom 26.10.2026 (Bild, Datumskachel, Eckdaten, Startort). */}
          <div>
            <EventDetailKopf
              bild={kopfBild}
              datum={kopfDatum}
              farbe={catColor}
              kicker={`${categoriesLabel}${eventTypeLabel ? ` · ${eventTypeLabel}` : ''}`}
              titel={event.title}
              abgesagt={event.cancelled}
                wann={kopfWann}
              status={kopfStatus}
              countdown={countdownLabel && !event.cancelled && !event.permanent ? countdownLabel : undefined}
              kacheln={kopfKacheln}
              ort={kopfOrt}
              blase={kopfBlase}
              beliebt={popular ? POPULAR_LABEL : undefined}
              strecken={kopfStrecken}
              gewaehlt={selectedDistanceIdx}
              onWahl={setSelectedDistanceIdx}
            >
              {((registrationChip && !mobileChipRedundant) || mobileAlertButton) ? (
                <>
                  {!mobileChipRedundant && registrationChip}
                  {mobileAlertButton}
              </>
              ) : null}
            </EventDetailKopf>
            <EventDetailUnten
              beschreibung={event.description}
              links={untenLinks(false)}
              unterkunft={untenUnterkunft}
              artikel={untenArtikel}
              veranstalter={untenVeranstalter}
            />
          </div>

          {/* Eingebettete Strecke (Komoot, AllTrails) ohne GPX: der Kopf zeigt sie nicht. */}
          {/* STRECKE — route/GPX for the selected distance only */}
          {(() => {
            const d = event.distances[selectedDistanceIdx];
            if (!d) return null;
            const placeholder = showRoutePlaceholder(event.distances, d);
            if (d.hasGpx || (!d.routeUrl && !placeholder)) return null;
            // Zeit-Variante mit Rundenlänge (distanceKm > 0) zeigt beides: "24 h · 8.1 km Runde".
            const gpxCore = isTimeVariant(d)
              ? `${formatDurationMinutes(d.durationMinutes!)}${d.distanceKm > 0 ? ` · ${d.distanceKm} km Runde` : ''}`
              : `${formatKm(d.distanceKm)} km`;
            const gpxTitle = `${d.label ? `${d.label} — ` : ''}${gpxCore}${formatElevationMeters(d.elevationMeters) ? ` · ${formatElevationMeters(d.elevationMeters)}` : ''}`;
            const embed = d.routeUrl ? buildRouteEmbed(d.routeUrl) : null;
            const platformLabel = embed?.platform === 'alltrails' ? 'AllTrails' : embed?.platform === 'komoot' ? 'Komoot' : null;
            return (
              <div key={d.id} style={{ ...cardStyle, overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderBottom: '1px solid #E8E4F0', gap: 12 }}>
                  <span style={{ ...monoLabel, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>STRECKE · {gpxTitle}</span>
                  {d.routeUrl && (
                    <a href={toExternalUrl(d.routeUrl || '')} target="_blank" rel="noopener noreferrer"
                      style={{ fontWeight: 600, fontSize: 13, color: '#922C57', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      {platformLabel ? `In ${platformLabel} öffnen →` : 'Öffnen →'}
                    </a>
                  )}
                </div>
                {placeholder ? (
                  <RouteMapPlaceholder />
                ) : d.hasGpx ? (
                  <GpxMap gpxUrl={gpxUrlFor(d.id)} title={gpxTitle} routeUrl={d.routeUrl || undefined} />
                ) : embed?.embedUrl ? (
                  <iframe src={embed.embedUrl} title={`Strecke ${distanceShortLabel(d)}`}
                    width="100%" height={480} frameBorder="0" scrolling="no" style={{ display: 'block' }} />
                ) : (
                  <div style={{ padding: '18px 20px' }}>
                    <a href={toExternalUrl(d.routeUrl || '')} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 14, fontWeight: 600, color: '#922C57', textDecoration: 'none', wordBreak: 'break-all' }}>
                      {embed?.platform === 'alltrails' ? 'Strecke auf AllTrails ansehen →' : d.routeUrl}
                    </a>
                  </div>
                )}
              </div>
            );
          })()}

          {(event.priceTiers?.length ?? 0) > 0 && (
            <div style={{ ...cardStyle, padding: 20, marginTop: 18 }}>
              <div style={monoLabel}>PREISSTUFEN</div>
              <PriceTierList tiers={event.priceTiers} />
            </div>
          )}
          {adminInfo && <div style={{ marginTop: 18 }}>{adminInfo}</div>}
          {statsCard && <div style={{ marginTop: 22 }}>{statsCard}</div>}
          {/* Der Hinweis auf Korrekturen ganz am Ende, nach Unterkunft und Preisen. */}
          <button type="button" className="event-edit-hint" style={{ width: '100%' }} onClick={startEditing}>Stimmt etwas nicht? <span>Schlage eine Änderung vor.</span></button>
        </>) : (<>
          {/* Rechner (29.10.2026): Kopf, links Strecke und Abschnitte, rechts die mitlaufende
              Anmeldekarte. Aufbau und Vorbilder in components/EventDetailDesktop.tsx. */}
          {/* Mit Foto steht der Kopf über beiden Spalten (das Titelbild braucht die Breite);
              ohne Foto links in der Hauptspalte, und die rechte Spalte rückt ganz nach oben. */}
          {desktopFoto && desktopKopf}
          <div className="edd-raster">
            <main className="edd-haupt">
              {!desktopFoto && desktopKopf}
              {/* Zahlen für Redaktion und Verwalter ganz oben: genau damit öffnen sie die Seite.
                  Besucher sehen die Karte nie. */}
              {statsCard}
              <EventStreckenBlock
                id="event-strecke"
                strecken={kopfStrecken}
                gewaehlt={selectedDistanceIdx}
                onWahl={setSelectedDistanceIdx}
                kennzahlen={desktopKennzahlen}
                bild={desktopBild}
                abgesagt={event.cancelled}
                routeLink={desktopRouteLink}
              />
              <EventDetailUnten
                beschreibung={event.description}
                links={untenLinks(true)}
                flyerBild={flyerIsImage && event.flyerUrl ? { url: event.flyerUrl, alt: `Flyer: ${event.title}` } : null}
                artikel={untenArtikel}
              />
              {adminInfo}
              <button type="button" className="event-edit-hint" onClick={startEditing}>Stimmt etwas nicht? <span>Schlage eine Änderung vor.</span></button>
            </main>
            {/* Rechts, was man zum Hinfahren braucht: Anmeldung, dann Unterkunft (wird Wochen
                vorher gebucht, deshalb über dem Wetter), das Wetter (erst sieben Tage vorher) und
                wer veranstaltet. */}
            <aside className="edd-seite">
              <div ref={setKarteEl}>
              <EventAnmeldeKarte
                termin={desktopTermin}
                terminZusatz={event.endDate && event.endDate > event.eventDate && !event.permanent ? `bis ${formatDate(event.endDate)}` : undefined}
                start={event.startTime ? `${event.startTime}${event.startTimeEnd ? ` bis ${event.startTimeEnd}` : ''} Uhr` : undefined}
                ort={kopfOrt}
                teilnehmer={participantsLabel || undefined}
                anmeldung={!mobileChipRedundant ? registrationChip : null}
                preisstufen={(event.priceTiers?.length ?? 0) > 0 ? <PriceTierList tiers={event.priceTiers} /> : null}
                primary={mobilePrimaryAction}
                primaryNode={nextEditionAlert && !desktopStatus?.aktion ? <RegistrationAlertButton event={event} nextEdition variant="primary" onEnabled={onAlertEnabled} /> : undefined}
                leise={showRegistrationButton && registrationButtonQuiet}
                glocke={mobileAlertButton}
                karte={desktopOrtKarte}
              />
              </div>
              {accommodationCard(event) && <div id={ABSCHNITT_IDS.unterkunft}><AccommodationCard event={event} /></div>}
              {desktopWetter}
              {untenVeranstalter && <EventVeranstalterKarte veranstalter={untenVeranstalter} id={ABSCHNITT_IDS.veranstalter} />}
            </aside>
          </div>

          <EventSprungLeiste
            sichtbar={kopfAusSicht}
            titel={event.title}
            termin={desktopTermin}
            abschnitte={desktopAbschnitte}
            primary={mobilePrimaryAction}
            leise={showRegistrationButton && registrationButtonQuiet}
            mitKnopf={karteAusSicht}
          />
        </>)}

        {/* Change history (audit trail) — Admins & Moderators only */}
        {isAdminOrModerator() && <EventHistory eventId={event.id} />}
        </>)}

        {/* Spacer so content isn't hidden behind the phone bottom bar */}
        <div className="hide-desktop" style={{ height: 88 }} />
      </div>



      {/* Delete confirmation modal */}
      {showReEnrichModal && event && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowReEnrichModal(false)}>
          <div className="surface-card rounded-2xl p-6 max-w-md w-full"
            onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
              Re-Enrich anfragen
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
              Reiht „{event.title}" in die Queue des Agenten ein. Beim nächsten Polling-Zyklus
              wird das Event prioritär erneut gescraped.
            </p>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
              Quelle
            </label>
            <select value={reEnrichSource} onChange={e => setReEnrichSource(e.target.value)}
              className="w-full px-3 py-2 mb-3 rounded-lg text-sm"
              style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }}>
              <option value="openclaw-event-enricher">OpenClaw — Event Enricher</option>
              <option value="openclaw-organizer-enricher">OpenClaw — Organizer Enricher</option>
              <option value="openclaw-prorace-enricher">OpenClaw — ProRace Enricher</option>
            </select>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
              Grund (optional)
            </label>
            <input value={reEnrichReason} onChange={e => setReEnrichReason(e.target.value)}
              placeholder="z.B. Neue Webseite vom Veranstalter"
              className="w-full px-3 py-2 mb-5 rounded-lg text-sm"
              style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }} />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowReEnrichModal(false)}
                className="px-4 py-2 rounded-xl text-sm font-semibold"
                style={{ background: 'var(--page-bg)', color: 'var(--text-secondary)', border: '1px solid var(--surface-border)' }}>
                Abbrechen
              </button>
              <button
                disabled={reEnrichSubmitting}
                onClick={async () => {
                  setReEnrichSubmitting(true);
                  try {
                    const res = await eventService.enqueueEnrichment({
                      sourceKey: reEnrichSource,
                      entityType: 'EVENT',
                      entityId: event.id,
                      reason: reEnrichReason || undefined,
                    });
                    toast.success(`In Queue eingereiht (#${res.requestId})`);
                    setShowReEnrichModal(false);
                    setReEnrichReason('');
                  } catch (e: any) {
                    toast.error(`Konnte nicht einreihen: ${e?.response?.data?.error || e?.message || 'Fehler'}`);
                  } finally {
                    setReEnrichSubmitting(false);
                  }
                }}
                className="px-4 py-2 bg-brand-600 text-white text-sm font-semibold rounded-xl hover:bg-brand-700 disabled:opacity-50">
                {reEnrichSubmitting ? 'Einreihen…' : 'Einreihen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowDeleteConfirm(false)}>
          <div className="surface-card rounded-2xl p-6 max-w-sm w-full"
            onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Event löschen?</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
              „{event?.title}" wird unwiderruflich gelöscht.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 rounded-xl text-sm font-semibold"
                style={{ background: 'var(--page-bg)', color: 'var(--text-secondary)', border: '1px solid var(--surface-border)' }}>
                Abbrechen
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white"
                style={{ background: deleting ? '#fca5a5' : '#ef4444' }}>
                {deleting ? 'Löschen...' : 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phone-only sticky bottom action bar — nicht ueber dem Formular, dort stehen die Knoepfe
          unten im Formular selbst. */}
      {!editing && handy && (
      <div className="app-bottombar hide-desktop ed-bottom-actions">
        <EventDetailActions
          primary={mobilePrimaryAction}
          primaryNode={nextEditionAlert ? <RegistrationAlertButton event={event} nextEdition variant="primary" onEnabled={onAlertEnabled} /> : undefined}
          favorited={!!event.favorited} onFavorite={handleFavorite}
          calendar={event.permanent ? undefined : {
            url: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/api/v1/events/${event.id}/ical`,
            filename: `${event.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.ics`,
            onClick: () => umamiTrack('event-ical', { id: event.id, titel: event.title }),
          }}
        />
      </div>
      )}

      {notifyDialog}

    </div>
  );
};

export default EventDetail;
